import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import type { Api, ClientState, LoginRequest, LoginResult, RegisterRequest, SwitchSubaccountResponse } from '../api';

export type AuthStatus = 'restoring' | 'signed_out' | 'signed_in';

interface AuthContextValue {
  api: Api;
  status: AuthStatus;
  state: ClientState;
  /** Set when a session ended unexpectedly or sign-out could not reach the server. */
  notice: string | null;
  login: (body: LoginRequest) => Promise<LoginResult>;
  register: (body: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  switchSubaccount: (agencyId: string, subaccountId: string) => Promise<SwitchSubaccountResponse>;
  exitSubaccount: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ api, children }: { api: Api; children: ReactNode }) {
  const state = useSyncExternalStore(api.client.subscribe, api.client.getState);
  const [restoring, setRestoring] = useState(() => api.client.needsRestore);
  const [notice, setNotice] = useState<string | null>(null);

  // Resume a stored session after a reload: mint an access token from the refresh token.
  useEffect(() => {
    if (!api.client.needsRestore) return;
    let active = true;
    api.client
      .refresh()
      .catch((err: unknown) => {
        console.warn('[auth] could not resume the stored session', err);
        if (active) setNotice('Your session has ended. Please sign in again.');
      })
      .finally(() => {
        if (active) setRestoring(false);
      });
    return () => {
      active = false;
    };
  }, [api]);

  // A session that ends mid-use (refresh rejected) drops the user to sign-in with a notice.
  const intentionalSignOut = useRef(false);
  const wasAuthenticated = useRef(state.authenticated);
  useEffect(() => {
    if (wasAuthenticated.current && !state.authenticated && !intentionalSignOut.current) {
      setNotice('Your session has ended. Please sign in again.');
    }
    if (state.authenticated) intentionalSignOut.current = false;
    wasAuthenticated.current = state.authenticated;
  }, [state.authenticated]);

  const login = useCallback(
    async (body: LoginRequest) => {
      const result = await api.auth.login(body);
      setNotice(null);
      return result;
    },
    [api]
  );

  const register = useCallback(
    async (body: RegisterRequest) => {
      await api.auth.register(body);
      setNotice(null);
    },
    [api]
  );

  const logout = useCallback(async () => {
    intentionalSignOut.current = true;
    setNotice(null);
    try {
      await api.auth.logout();
    } catch (err) {
      console.error('[auth] sign-out could not be confirmed by the server', err);
      setNotice('You are signed out on this device, but the server could not confirm it.');
    }
  }, [api]);

  const switchSubaccount = useCallback(
    (agencyId: string, subaccountId: string) => api.client.switchSubaccount(agencyId, subaccountId),
    [api]
  );
  const exitSubaccount = useCallback(() => api.client.exitSubaccount(), [api]);

  const status: AuthStatus = restoring ? 'restoring' : state.authenticated ? 'signed_in' : 'signed_out';

  const value = useMemo<AuthContextValue>(
    () => ({
      api,
      status,
      state,
      notice,
      login,
      register,
      logout,
      switchSubaccount,
      exitSubaccount,
    }),
    [api, status, state, notice, login, register, logout, switchSubaccount, exitSubaccount]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>');
  return value;
}
